import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { ROUTES } from 'src/constants/routes.const';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { MeApi, UserDictionaryApi, WordlistsApi } from 'src/api';
import { API_TARGET_ITEM_TYPES } from 'src/constants/itemTypes';
import { convertUrlToTriggerRefresh } from 'src/utils/convertUrlToTriggerRefresh';
import { useUserContext } from 'src/context/user';
import { Lesson, RecommendedLesson } from '../types';

// Experiment release date - users who joined after this date will see the new title format
const LEARN_TITLE_EXPERIMENT_RELEASE_DATE = '2025-08-29T12:00:00Z';

const msgs = defineMessages({
  noLearn: { id: 'learnq_endState_message_noLearn' },
  noReview: { id: 'learnq_endState_message_noReview' },
  noVideo: { id: 'learnq_endState_message_noVideos' },
  noConversations: { id: 'learnq_endState_message_noConversations' },
  noPronunciation: { id: 'learnq_endState_message_noPronunciation' },
  learnWords: { id: 'learnq_widget_title_learn' },
  start: { id: 'learnq_widget_CTA' },
  learn: { id: 'learnq_lessons_preview_title_learn2' },
});

export const useRecommendedLesson = (lesson: Lesson | undefined) => {
  const languagePairId = getLanguagePairId();
  const intl = useIntl();
  const { user } = useUserContext();
  const { pathname, query } = useRouter();

  const { data: wordlistsData, isError: isErrorWordlists } = useQuery({
    queryKey: ['getWordlists', languagePairId],
    queryFn: () => WordlistsApi.getWordlists(languagePairId as number),
    enabled: !!languagePairId && lesson?.type === 'learn',
  });

  const { data: recommendations, isError: isErrorRecommendations } = useQuery({
    queryKey: ['recommendations', languagePairId],
    queryFn: () => MeApi.getRecommendations(languagePairId as number),
    enabled:
      !!languagePairId &&
      (lesson?.type === 'pronunciation' ||
        lesson?.type === 'video' ||
        lesson?.type === 'conversation'),
  });

  const { data: reviewData, isError: isErrorReview } = useQuery({
    queryKey: ['practice', languagePairId],
    queryFn: () => MeApi.getPractice(languagePairId as number),
    enabled: !!languagePairId && lesson?.type === 'review',
  });

  const { data: pronunciationData, isError: isErrorPronunciation } = useQuery({
    queryKey: ['recent_learnables', languagePairId],
    queryFn: () =>
      UserDictionaryApi.getRandomKnownLearnables(languagePairId as number, {
        typesToExclude: [API_TARGET_ITEM_TYPES.ALPHABET],
        forPronunciation: true,
      }),
    enabled: !!languagePairId && lesson?.type === 'pronunciation',
  });

  const recommendedLesson = useMemo((): RecommendedLesson | null => {
    if (!lesson) return null;

    const languagePairId = getLanguagePairId();
    /* istanbul ignore if: should be defined */
    if (!languagePairId) return null;

    const backUrl = encodeURIComponent(
      query.back
        ? String(query.back)
        : pathname === ROUTES.dashboard
          ? ROUTES.dashboard
          : ROUTES.myLessons,
    );

    switch (lesson.type) {
      /* istanbul ignore next: please test in WEBSITE-7219 */
      case 'learn': {
        if (!wordlistsData) return null;

        const activeWordlist = wordlistsData.wordlists.find(
          w => w.wordlistId === wordlistsData.activeWordlistId,
        );
        if (
          !activeWordlist ||
          activeWordlist.isCompleted ||
          (!user?.isPro && activeWordlist.isLocked) ||
          (activeWordlist.creator === 'user' && activeWordlist.learnablesCount === 0)
        ) {
          const availableWordlists = wordlistsData.wordlists.filter(
            w =>
              !w.isCompleted &&
              (user?.isPro || !w.isLocked) &&
              !(w.creator === 'user' && w.learnablesCount === 0),
          );
          const isEmptyUserWordlistOnly =
            activeWordlist?.creator === 'user' &&
            activeWordlist?.learnablesCount === 0 &&
            availableWordlists.length === 0;

          return {
            ...lesson,
            enabled: false,
            disabledReason: intl.formatMessage(msgs.noLearn),
            alternativeLesson: isEmptyUserWordlistOnly
              ? undefined
              : {
                  ctaText: intl.formatMessage(msgs.start),
                  url: `${ROUTES.wordlists.choose}?back=${backUrl}`,
                },
          };
        }

        const isNewUser =
          user && Date.parse(user.dateJoined) > Date.parse(LEARN_TITLE_EXPERIMENT_RELEASE_DATE);
        const learnTitle = isNewUser
          ? `${intl.formatMessage(msgs.learn, { wordlist: activeWordlist.title })}`
          : lesson.title;

        return {
          ...lesson,
          title: learnTitle,
          enabled: true,
          url: convertUrlToTriggerRefresh(
            `${ROUTES.aprender.learn}?language_pair_id=${languagePairId}&back=${backUrl}`,
          ),
        };
      }

      case 'review': {
        if (!reviewData) return null;

        if (!reviewData.difficultWords.enabled && !reviewData.review.enabled) {
          return {
            ...lesson,
            enabled: false,
            disabledReason: intl.formatMessage(msgs.noReview),
          };
        }

        return {
          ...lesson,
          enabled: true,
          url: `${ROUTES.aprender.selectReview}?back=${backUrl}`,
        };
      }

      case 'video': {
        if (!recommendations) return null;

        if (!recommendations.immerse) {
          return {
            ...lesson,
            enabled: false,
            disabledReason: intl.formatMessage(msgs.noVideo),
          };
        }

        return {
          ...lesson,
          enabled: true,
          url: `${ROUTES.video.media}?contentMediaId=${recommendations.immerse.contentMediaId}&languagePairId=${languagePairId}&back=${backUrl}`,
        };
      }

      case 'conversation': {
        if (!recommendations) return null;

        if (!recommendations.communicate) {
          return {
            ...lesson,
            enabled: false,
            disabledReason: intl.formatMessage(msgs.noConversations),
          };
        }

        return {
          ...lesson,
          enabled: true,
          url: `${ROUTES.membot.dialog}?slug=${recommendations.communicate.locationSlug}&language_pair=${languagePairId}&back=${backUrl}`,
        };
      }

      case 'sentence_builder':
        return {
          ...lesson,
          enabled: true,
          url: `${ROUTES.ai.dialog}?type=sentence-builder&back=${backUrl}`,
        };

      case 'pronunciation': {
        if (!pronunciationData || !recommendations) return null;

        if (pronunciationData.learnables.length === 0) {
          return {
            ...lesson,
            enabled: false,
            disabledReason: intl.formatMessage(msgs.noPronunciation),
            alternativeLesson: recommendations.learn?.nextScenario
              ? {
                  ctaText: intl.formatMessage(msgs.learnWords),
                  url: `${ROUTES.aprender.learn}?scenario_id=${recommendations.learn.nextScenario.scenarioId}&back=${backUrl}`,
                }
              : undefined,
          };
        }

        return {
          ...lesson,
          enabled: true,
          url: `${ROUTES.ai.dialog}?type=pronunciation&back=${backUrl}`,
        };
      }
    }
  }, [
    intl,
    lesson,
    wordlistsData,
    recommendations,
    reviewData,
    pronunciationData,
    pathname,
    query.back,
    user,
  ]);

  const isError =
    isErrorWordlists || isErrorRecommendations || isErrorReview || isErrorPronunciation;

  return {
    recommendedLesson,
    isLoading: lesson === undefined ? false : !isError && !recommendedLesson,
    isError,
  };
};
